import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketData } from '@/hooks/useMarketData';
import { useLeads } from '@/hooks/useLeads';
import { useSharedOffers } from '@/hooks/useSharedOffers';
import { formatPriceCt, formatRelativeTime, formatDate } from '@/utils/formatters';
import {
  Users, FileText, TrendingUp, TrendingDown, Minus,
  Plus, DollarSign, BarChart3, ArrowUpRight, Clock,
  Eye, CheckCircle, Activity, Zap, Calendar,
  AlertTriangle, RefreshCw
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, subtext, trend, trendValue, iconBg, onClick }) => (
  <div className="stat-card cursor-pointer" onClick={onClick}>
    <div className="flex items-start justify-between">
      <div>
        <p className="stat-label">{label}</p>
        <p className="stat-value">{value}</p>
        <div className="flex items-center gap-2 mt-2">
          {trend === 'up' && <span className="flex items-center gap-1 text-xs font-semibold text-success"><ArrowUpRight size={12} />{trendValue}</span>}
          {trend === 'down' && <span className="flex items-center gap-1 text-xs font-semibold text-destructive"><TrendingDown size={12} />{trendValue}</span>}
          {subtext && <span className="text-xs text-muted-foreground">{subtext}</span>}
        </div>
      </div>
      <div className={`stat-icon ${iconBg}`}>
        <Icon size={22} />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { profile } = useAuth();
  const { marketData, isLoading, error: marketError, refetch: reloadMarket } = useMarketData();
  const { leads, stats: leadStats, error: leadsError, reload: reloadLeads } = useLeads();
  const { offers, stats: offerStats, activity: recentEvents, error: offersError, reload: reloadOffers } = useSharedOffers();
  const navigate = useNavigate();

  // Ladefehler duerfen NIE wie "keine Daten" aussehen. Bei Fehler zeigen wir
  // "–" statt "0", damit niemand eine Null als echtes Ergebnis liest.
  const DASH = '–';

  const problems = [
    leadsError && { key: 'leads', label: 'Leads', message: leadsError },
    offersError && { key: 'offers', label: 'Angebote', message: offersError },
    marketError && { key: 'market', label: 'Marktdaten', message: marketError },
  ].filter(Boolean);

  const retryAll = () => {
    if (leadsError) reloadLeads();
    if (offersError) reloadOffers();
    if (marketError) reloadMarket();
  };

  // marketData ist bei Fehler null — der Hook liefert bewusst keine
  // simulierten Ersatzwerte mehr. Also gibt es hier auch nichts anzuzeigen.
  const currentPrice = marketError || typeof marketData?.basePrice !== 'number'
    ? null
    : formatPriceCt(marketData.basePrice * 100);
  const trend = marketError ? undefined : marketData?.trend;

  const volatility = marketError ? null : marketData?.marketAnalysis?.volatility;

  const upcomingActions = (leadsError ? [] : leads)
    .filter(l => l.next_action && l.next_action_date)
    .sort((a, b) => a.next_action_date.localeCompare(b.next_action_date))
    .slice(0, 5);


  const today = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Fehlerbanner — Ladefehler sichtbar machen, nicht als leere Daten tarnen */}
      {problems.length > 0 && (
        <div
          role="alert"
          className="flex flex-wrap items-start gap-3 rounded-lg border border-destructive/30 bg-[#ffe6e6] px-4 py-3"
        >
          <AlertTriangle size={18} className="text-[#cc0000] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[16rem]">
            <p className="text-sm font-semibold text-[#cc0000]">
              {problems.length === 1
                ? `${problems[0].label} konnten nicht geladen werden`
                : 'Mehrere Bereiche konnten nicht geladen werden'}
            </p>
            <ul className="mt-1 space-y-0.5">
              {problems.map(p => (
                <li key={p.key} className="text-xs text-[#cc0000]/90">
                  <span className="font-medium">{p.label}:</span> {p.message}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#cc0000]/90 mt-1.5">
              Die betroffenen Kennzahlen werden als „{DASH}“ angezeigt. Es handelt sich um einen
              Ladefehler, nicht um fehlende Daten — bitte keine Zahlen aus diesem Bereich verwenden.
            </p>
          </div>
          <button
            type="button"
            onClick={retryAll}
            className="flex items-center gap-1.5 rounded-lg border border-[#cc0000]/30 bg-card px-3 py-1.5 text-xs font-semibold text-[#cc0000] hover:bg-[#ffd6d6] transition-colors"
          >
            <RefreshCw size={13} />
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="pg-title">Willkommen zurueck, {profile?.full_name?.split(' ')[0] || 'Demo'}</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
            <Calendar size={14} />
            {today}
          </p>
        </div>
        <button
          onClick={() => navigate('/app/leads')}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors shadow-sm"
        >
          <Plus size={16} />
          Neuer Lead anlegen
        </button>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          icon={Users}
          label="Offene Leads"
          value={leadsError ? DASH : leadStats.offen}
          subtext={leadsError ? 'Nicht geladen' : `${leadStats.neu} im Status "Neu"`}
          iconBg="bg-accent text-primary"
          onClick={() => navigate('/app/leads')}
        />
        <StatCard
          icon={FileText}
          label="Offene Angebote"
          value={offersError ? DASH : offerStats.offen}
          subtext={offersError ? 'Nicht geladen' : `${offerStats.beauftragt + offerStats.vollmacht} beauftragt`}
          iconBg="bg-[#e6fbf2] text-[#00a864]"
          onClick={() => navigate('/app/angebote')}
        />
        <StatCard
          icon={DollarSign}
          label="Boersenpreis EPEX"
          value={currentPrice === null ? DASH : `${currentPrice} ct`}
          subtext={
            marketError ? 'Nicht verfuegbar'
              : isLoading ? 'Wird geladen…'
              : trend === 'up' ? 'Steigend'
              : trend === 'down' ? 'Fallend'
              : 'Stabil'
          }
          trend={trend}
          iconBg={trend === 'up' ? 'bg-[#ffe6e6] text-[#cc0000]' : trend === 'down' ? 'bg-[#e6fbf2] text-[#00a864]' : 'bg-[#e8fcfd] text-[#0ea5b8]'}
        />
        <StatCard
          icon={Activity}
          label="Marktvolatilitaet"
          value={typeof volatility === 'number' ? `${(volatility * 100).toFixed(2)} ct` : DASH}
          subtext={marketError ? 'Nicht verfuegbar' : 'Schwankung, 7 Tage'}
          iconBg="bg-[#fff8e0] text-[#b8860b]"
          onClick={() => navigate('/app/markt')}
        />
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Actions */}
        <div className="lg:col-span-2">
          <div className="hk-card">
            <div className="hk-card-header">
              <h6>Ihre naechsten Aktionen</h6>
              <button onClick={() => navigate('/app/leads')} className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
                Alle Leads <ArrowUpRight size={14} />
              </button>
            </div>
            <div className="hk-card-body">
              {leadsError ? (
                <div className="text-center py-8">
                  <AlertTriangle className="mx-auto text-[#cc0000] mb-2" size={32} />
                  <p className="text-sm font-medium text-[#cc0000]">Leads konnten nicht geladen werden</p>
                  <p className="text-xs text-muted-foreground mt-1">{leadsError}</p>
                  <button
                    type="button"
                    onClick={reloadLeads}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors"
                  >
                    <RefreshCw size={13} />
                    Erneut versuchen
                  </button>
                </div>
              ) : upcomingActions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="mx-auto text-muted-foreground mb-2" size={32} />
                  <p className="text-sm text-muted-foreground">Keine faelligen Aktionen</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {upcomingActions.map(lead => {
                    const isOverdue = lead.next_action_date < new Date().toISOString().slice(0, 10);
                    return (
                      <div
                        key={lead.id}
                        onClick={() => navigate(`/app/leads/${lead.id}`)}
                        className="flex items-center justify-between py-3.5 first:pt-0 last:pb-0 cursor-pointer group"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isOverdue ? 'bg-[#ffe6e6] text-[#cc0000]' : 'bg-accent text-primary'
                          }`}>
                            <Clock size={16} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                              {lead.next_action}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {lead.company?.name} — {lead.contact?.first_name} {lead.contact?.last_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 ml-2">
                          <span className={`text-xs font-medium ${isOverdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {formatDate(lead.next_action_date)}
                          </span>
                          <span className={`hk-badge ${lead.status === 'neu' ? 'info' : lead.status === 'qualifiziert' ? 'primary' : 'success'}`}>
                            {lead.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div>
          <div className="hk-card">
            <div className="hk-card-header">
              <h6>Aktivitaeten</h6>
              {offersError
                ? <span className="hk-badge danger">Fehler</span>
                : <span className="hk-badge primary">Live</span>}
            </div>
            <div className="hk-card-body">
              {offersError ? (
                <div className="text-center py-8">
                  <AlertTriangle className="mx-auto text-[#cc0000] mb-2" size={32} />
                  <p className="text-sm font-medium text-[#cc0000]">Aktivitaeten konnten nicht geladen werden</p>
                  <p className="text-xs text-muted-foreground mt-1">{offersError}</p>
                  <button
                    type="button"
                    onClick={reloadOffers}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors"
                  >
                    <RefreshCw size={13} />
                    Erneut versuchen
                  </button>
                </div>
              ) : recentEvents.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="mx-auto text-muted-foreground mb-2" size={32} />
                  <p className="text-sm text-muted-foreground">Noch keine Aktivitaeten</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentEvents.map(evt => {
                    const labels = { gesendet: 'Angebot versendet', beauftragt: 'Preisspanne bestaetigt', vollmacht: 'Vollmacht erteilt' };
                    const icons = { gesendet: FileText, beauftragt: CheckCircle, vollmacht: CheckCircle };
                    const colors = {
                      gesendet: 'bg-[#e8fcfd] text-[#0ea5b8]',
                      beauftragt: 'bg-[#fff8e0] text-[#b8860b]',
                      vollmacht: 'bg-[#e6fbf2] text-[#00a864]',
                    };
                    const EvtIcon = icons[evt.type] || Activity;
                    return (
                      <div
                        key={evt.id}
                        className="flex items-start gap-3 cursor-pointer"
                        onClick={() => navigate('/app/angebote')}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors[evt.type] || 'bg-muted text-muted-foreground'}`}>
                          <EvtIcon size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">{labels[evt.type] || evt.type}</p>
                          <p className="text-xs text-muted-foreground truncate">{evt.company_name}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{formatRelativeTime(evt.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="hk-card">
        <div className="hk-card-header">
          <h6>Schnellzugriff</h6>
        </div>
        <div className="hk-card-body">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Neuer Lead', desc: 'Kunde erfassen', icon: Plus, color: 'bg-accent text-primary border-primary/20', path: '/app/leads' },
              { label: 'Preise eintragen', desc: 'Versorgerpreise', icon: DollarSign, color: 'bg-[#e6fbf2] text-[#00a864] border-[#00D67F]/20', path: '/app/preise/eingabe' },
              { label: 'Lastgang-Analyse', desc: 'Verbrauch analysieren', icon: BarChart3, color: 'bg-[#e8fcfd] text-[#0ea5b8] border-[#18DDEF]/20', path: '/app/lastgang' },
              { label: 'Marktdaten', desc: 'EPEX Spot-Preise', icon: TrendingUp, color: 'bg-[#fff8e0] text-[#b8860b] border-[#FFC400]/20', path: '/app/markt' },
            ].map(action => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className={`flex items-start gap-3 p-4 rounded-lg border text-left hover:shadow-md transition-all ${action.color}`}
              >
                <div className="w-10 h-10 bg-card rounded-lg flex items-center justify-center flex-shrink-0">
                  <action.icon size={18} />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground">{action.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{action.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
